import {
  ChannelImportStatus,
  Connection,
  ConnectionStatusReasons,
  ConnectionTypes,
  defaultUndefinedRoomId,
} from '@tv3/store/connection/connection.model'
import * as moment from 'moment'
import { get, isNumber, max } from 'lodash'
import * as R from 'ramda'
import { Channel, ChannelNameTokens, Rental } from '@tokeet-frontend/tv3-platform'
import { isFeedChannel, isTiketLikeChannel } from '@tokeet-frontend/channels'

export enum ConnectionTypeTexts {
  API = 'API',
  Exported = 'Exported iCal',
  Imported = 'Imported iCal',
}

export enum ConnectionStatusTexts {
  Active = 'Active',
  Disabled = 'Disabled',
  Disconnected = 'Disconnected',
  Pending = 'Pending',
  Stale = 'Stale',
  Inactive = 'Inactive',
  Unlisted = 'Unlisted',
  Deleted = 'Deleted',
}

// Reasons a fresh OAuth grant cannot fix. Everything else is an authorization problem the user
// can clear by reconnecting -- including a missing reason, which is every connection disabled
// before status_reason existed. Those can never acquire one: a disabled property short-circuits
// before it ever calls the channel again, so nothing re-runs disableProperty to record why.
const nonReconnectableReasons: string[] = [ConnectionStatusReasons.HostSuspended]

export function isReconnectableReason(reason: string): boolean {
  return !nonReconnectableReasons.includes(reason)
}

export function getConnectionStatusReasonText(reason: string): string {
  switch (reason) {
    case ConnectionStatusReasons.AuthorizationRevoked:
      return $localize`This channel account was removed from the channel's side, so rates, availability and bookings are no longer syncing. Reconnect to resume.`
    case ConnectionStatusReasons.OauthMissing:
    case ConnectionStatusReasons.RefreshTokenMissing:
      return $localize`We no longer hold a valid authorization for this channel account, so nothing is syncing. Reconnect to resume.`
    case ConnectionStatusReasons.HostSuspended:
      return $localize`The channel suspended this account, so nothing is syncing. Contact the channel to resolve it.`
    default:
      return $localize`This connection is disabled, so rates and availability are not syncing. Reconnect to resume.`
  }
}

// A connection that is off, whatever the reason. Both status texts mean "not syncing".
export function isDisabledStatusText(statusText: ConnectionStatusTexts): boolean {
  return statusText === ConnectionStatusTexts.Disabled || statusText === ConnectionStatusTexts.Disconnected
}

export function getConnectionImportErrorText(reason: string): string {
  switch (reason) {
    case 'enqueue_failed':
      return $localize`We could not start the listing import for this connection, so some listings may be missing. Run the import again from the channel details.`
    case 'import_failed':
      return $localize`The listing import for this connection failed, so some listings may be missing. Run the import again from the channel details.`
    default:
      return $localize`The listing import for this connection did not complete. Run the import again from the channel details.`
  }
}

// Text for a listing-import job that is still running. Terminal states need no banner
// here: `failed` is covered by getConnectionImportErrorText and `done` is the quiet norm.
export function getConnectionImportProgressText(status: ChannelImportStatus): string {
  const imported = status?.imported || 0
  switch (status?.status) {
    case 'queued':
      return $localize`A listing import for this connection is queued.`
    case 'in_progress':
      return status.total
        ? $localize`Importing listings: ${imported} of ${status.total} imported so far.`
        : $localize`Importing listings for this connection.`
    default:
      return ''
  }
}

export class ConnectionView extends Connection {
  isApi: boolean
  isICal: boolean
  isFeedApi: boolean
  isExported: boolean
  isImported: boolean
  isCreatedOnTokeet: boolean
  isIncompleted: boolean
  isAirbnbSyncPaused: boolean
  isAirbnbDeleted: boolean
  statusText: ConnectionStatusTexts
  statusReasonText: string
  needsReconnect: boolean
  importErrorText: string
  importProgressText: string

  /* View Fields */
  linkDateView: string
  createdView: string
  typeText: string
  accountName: string
  warning = false
  channelFriendlyName: string
  customICalChannelName: string
  icalSourceView: string

  linkDateOrCreated: number
  linkDateOrCreatedView: string

  propertyNameView: string

  constructor(public channel: Channel, public rental: Rental, public connection: Connection) {
    super(connection)

    this.name = this.name || this.source
    this.isApi = R.pipe(R.defaultTo(''), R.toLower, R.equals(R.toLower(ConnectionTypes.Api)))(connection.type)
    this.isICal = !this.isApi
    this.isFeedApi = this.isApi && isFeedChannel(this.channel?.name)
    this.isImported = R.pipe(
      R.defaultTo(''),
      R.toLower,
      R.equals(R.toLower(ConnectionTypes.ImportedICal))
    )(connection.type)
    this.isExported = R.pipe(
      R.defaultTo(''),
      R.toLower,
      R.equals(R.toLower(ConnectionTypes.ExportedICal))
    )(connection.type)

    this.typeText =
      (this.isApi && ConnectionTypeTexts.API) ||
      (this.isImported && ConnectionTypeTexts.Imported) ||
      (this.isExported && ConnectionTypeTexts.Exported) ||
      ''

    const format = 'DD - MMM - YYYY'
    this.linkDateView =
      (!this.linkDate && 'Never') || (isNumber(this.linkDate) && moment(this.linkDate * 1000).format(format)) || '...'
    this.createdView =
      (!this.created && 'Never') || (isNumber(this.created) && moment(this.created * 1000).format(format)) || '...'

    this.customICalChannelName = R.pathOr(this.name || 'Unknown', ['attributes', 'channel_display_name'], this)
    if (channel) {
      // this.channelId = channel.id; // warning: the channel id in connection and channel maybe different.
      this.channelFriendlyName =
        channel.name === ChannelNameTokens.UnknownICal ? this.customICalChannelName : channel.friendlyName
    } else {
      this.channelFriendlyName = this.name
    }

    if (this.isImported) {
      this.icalSourceView = this.getIcalSourceView()
    }

    if (rental) {
      this.rentalName = rental.name
      this.lastratepush = max([this.lastratepush, get(rental, 'attributes.last_rateengine_update')])
    }
    this.accountName = this.propertyName || this.channelFriendlyName
    this.propertyNameView = (this.isApi && this.roomName) || (this.isImported && this.name) || 'N/A'
    const isAirbnbV2Connection = this.channel?.name === ChannelNameTokens.AirBnBV2API
    this.isAirbnbDeleted =
      isAirbnbV2Connection && (!!this.airbnbDeleted || this.syncPausedReason === 'deleted_on_airbnb')
    this.isAirbnbSyncPaused =
      isAirbnbV2Connection && (!!this.syncPaused || this.airbnbListed === false || this.isAirbnbDeleted)

    if (this.isApi) {
      this.isIncompleted = !this.propertyId || !this.roomId || this.roomId === defaultUndefinedRoomId
      this.linkDateOrCreated = this.linkDate
      this.linkDateOrCreatedView = this.linkDateView
    } else {
      this.linkDateOrCreated = this.created
      this.linkDateOrCreatedView = this.createdView
    }

    this.statusText = this.getStatusText()
    // Loose compare, to stay in step with getStatusText() -- status arrives as '0' from some payloads.
    // tslint:disable-next-line:triple-equals
    this.needsReconnect = this.isApi && this.status == 0 && isReconnectableReason(this.statusReason)
    this.statusReasonText = isDisabledStatusText(this.statusText)
      ? getConnectionStatusReasonText(this.statusReason)
      : ''
    this.importErrorText = this.importError ? getConnectionImportErrorText(this.importError) : ''
    this.importProgressText = getConnectionImportProgressText(this.importStatus)
    this.setWarning()
    this.lastsync = max([this.lastsync, this.lastavailpull, this.lastavailpush, this.lastratepush, this.created])
  }

  setWarning() {
    // An import failure warns even while the connection itself is Active -- the connection
    // works, but listings the user expects to see were never created.
    this.warning = this.statusText !== ConnectionStatusTexts.Active || !!this.importError
  }

  // Derives the iCal source domain for "Unknown" imported calendars. The stored url may be
  // missing a scheme (e.g. iCloud published calendars saved as "p132-caldav.icloud.com/..."),
  // which makes `new URL()` throw. A single bad url here used to crash selectAllConnectionViews
  // and blank the entire Channels page, so normalize the scheme and never let it throw.
  private getIcalSourceView(): string {
    if (this.source !== ChannelNameTokens.UnknownICal) {
      return this.source
    }
    try {
      const raw = this.url || ''
      const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`
      return new URL(normalized).hostname.split('.').slice(-2).join('.')
    } catch {
      return this.customICalChannelName || this.source || 'Unknown'
    }
  }

  private getStatusText(): ConnectionStatusTexts {
    if (this.channel?.name === ChannelNameTokens.Hvmi) {
      return this.status_text as ConnectionStatusTexts
    }
    if (isTiketLikeChannel(this.channel?.name)) {
      this.status = 1
    }

    if (this.isApi) {
      if (this.isAirbnbSyncPaused) {
        if (this.isAirbnbDeleted) {
          return ConnectionStatusTexts.Deleted
        }
        if (this.syncPausedReason === 'unlisted_on_airbnb') {
          return ConnectionStatusTexts.Unlisted
        }
        return ConnectionStatusTexts.Inactive
      }

      // tslint:disable-next-line:triple-equals
      if (this.status == 0) {
        return this.statusReason === ConnectionStatusReasons.AuthorizationRevoked
          ? ConnectionStatusTexts.Disconnected
          : ConnectionStatusTexts.Disabled
      }

      // TV4-6062: the mapped room no longer exists on the channel. Sync stamps from when the room
      // was alive would otherwise still satisfy isApiConnectionActive and render the row Active.
      if (this.roomMissing) {
        return ConnectionStatusTexts.Pending
      }

      if (isApiConnectionActive(this, this) || isFeedChannel(this.channel?.name)) {
        return ConnectionStatusTexts.Active
      } else {
        return ConnectionStatusTexts.Pending
      }
    } else {
      if (!this.lastsync) {
        return ConnectionStatusTexts.Pending
      } else if (R.is(Number, this.status) && this.status <= 0) {
        return ConnectionStatusTexts.Disabled
      }

      const now = moment.utc().unix()
      let failed, pending
      if (this.isImported) {
        failed = 24 * 60 * 60
        pending = 6 * 60 * 60
      } else {
        failed = 4 * 24 * 60 * 60 // iCal is stale after 4 days of not being fetched by the channel.
        pending = 24 * 60 * 60
      }

      if (now >= this.lastsync + failed) {
        return ConnectionStatusTexts.Stale
      } else if (now < this.lastsync + pending) {
        return ConnectionStatusTexts.Active
      }

      return ConnectionStatusTexts.Pending
    }
  }
}

export function isApiConnectionActive(
  connection: Connection,
  status: { lastavailpull?: number; lastavailpush?: number; lastratepush?: number }
): boolean {
  if (!connection.rentalId) {
    return false
  }

  if (connection.name === ChannelNameTokens.BookingAPI) {
    return !!(status && status.lastavailpull && status.lastavailpush && status.lastratepush)
  }

  if (isTiketLikeChannel(connection.name)) {
    return !!(status && status.lastavailpush && status.lastratepush)
  }

  return !!(status && (status.lastavailpull || status.lastavailpush || status.lastratepush))
}
